import { useCallback, useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
}

function toDateInputValue(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function startOfCalendarQuarter(d) {
  const qStartMonth = Math.floor(d.getMonth() / 3) * 3
  return new Date(d.getFullYear(), qStartMonth, 1)
}

function startOfNextCalendarQuarter(d) {
  const cur = startOfCalendarQuarter(d)
  return new Date(cur.getFullYear(), cur.getMonth() + 3, 1)
}

function formatShortDate(iso) {
  if (!iso) return ""
  const d = new Date(iso)
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

// Guided rollover: (1) pick when the new 13-week cycle begins — server previews
// the cycle label and dates — (2) name your first rock before the flow ends.
export function StartNewCycleDialog({ open, onOpenChange }) {
  const [step, setStep] = useState(1)
  const [startedOn, setStartedOn] = useState(() => toDateInputValue(new Date()))
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const [createBusy, setCreateBusy] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [newCycle, setNewCycle] = useState(null)

  const [firstRockTitle, setFirstRockTitle] = useState("")
  const [rockBusy, setRockBusy] = useState(false)
  const [rockError, setRockError] = useState(null)

  const reset = useCallback(() => {
    setStep(1)
    setStartedOn(toDateInputValue(new Date()))
    setPreview(null)
    setPreviewError(null)
    setPreviewLoading(false)
    setCreateBusy(false)
    setCreateError(null)
    setNewCycle(null)
    setFirstRockTitle("")
    setRockBusy(false)
    setRockError(null)
  }, [])

  useEffect(() => {
    if (!open) return
    reset()
  }, [open, reset])

  const loadPreview = useCallback(async (dateStr) => {
    if (!dateStr) return
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const res = await fetch(
        `/app/member/cycle_rollovers/preview?started_on=${encodeURIComponent(dateStr)}`,
        {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        },
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setPreview(null)
        setPreviewError(json.error || "Could not load preview.")
        return
      }
      setPreview({
        quarterKey: json.quarterKey,
        startedAt: json.startedAt,
        endedAt: json.endedAt,
      })
    } catch {
      setPreview(null)
      setPreviewError("Network error loading preview.")
    } finally {
      setPreviewLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open || step !== 1) return
    const t = setTimeout(() => loadPreview(startedOn), 250)
    return () => clearTimeout(t)
  }, [open, step, startedOn, loadPreview])

  const applyPreset = (d) => {
    setStartedOn(toDateInputValue(d))
  }

  const submitCreate = async () => {
    if (createBusy || previewError || !preview) return
    setCreateBusy(true)
    setCreateError(null)
    try {
      const res = await fetch("/app/member/cycle_rollovers", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ started_on: startedOn }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setCreateError(json.error || "Could not start a new cycle.")
        return
      }
      setNewCycle(json.newCycle)
      setStep(2)
    } catch {
      setCreateError("Network error. Please try again.")
    } finally {
      setCreateBusy(false)
    }
  }

  const saveFirstRock = async () => {
    if (!newCycle?.id || rockBusy) return
    const title = firstRockTitle.trim()
    if (!title) {
      setRockError("Give this rock a short title, or skip for now.")
      return
    }
    setRockBusy(true)
    setRockError(null)
    const rock = {
      id: crypto.randomUUID(),
      title,
      status: "on_track",
      progress_pct: 0,
      milestones: [],
      result: null,
    }
    try {
      const res = await fetch(`/app/planning/quarterly_check_ins/${newCycle.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json, text/html",
        },
        credentials: "same-origin",
        body: JSON.stringify({ rocks: [rock] }),
      })
      if (!res.ok) {
        const json = await res.json().catch(() => ({}))
        setRockError(json.error || "Couldn't save the rock.")
        return
      }
      router.reload({ preserveScroll: true })
      onOpenChange(false)
    } catch {
      setRockError("Network error. Please try again.")
    } finally {
      setRockBusy(false)
    }
  }

  const finishWithoutRock = () => {
    router.reload({ preserveScroll: true })
    onOpenChange(false)
  }

  const today = new Date()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" showCloseButton={!createBusy && !rockBusy}>
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle>Start a new quarter</DialogTitle>
              <DialogDescription className="text-zinc-600">
                Your current cycle will close and a fresh 13-week cycle begins on the date you
                choose. KPIs, wheel, and annual plan stay as-is — you&apos;ll set new quarter rocks.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <p className="text-sm font-medium text-zinc-800">When does the new cycle begin?</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => applyPreset(today)}
                >
                  Today
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => applyPreset(startOfCalendarQuarter(today))}
                >
                  Start of this calendar quarter
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => applyPreset(startOfNextCalendarQuarter(today))}
                >
                  Start of next calendar quarter
                </Button>
              </div>
              <Input
                type="date"
                value={startedOn}
                onChange={(e) => setStartedOn(e.target.value)}
                className="max-w-xs"
                disabled={createBusy}
              />
              <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
                {previewLoading && <p className="text-zinc-500">Loading preview…</p>}
                {!previewLoading && previewError && (
                  <p className="text-red-800">{previewError}</p>
                )}
                {!previewLoading && !previewError && preview && (
                  <div className="space-y-1">
                    <p>
                      <span className="font-semibold text-zinc-900">{preview.quarterKey}</span>
                      <span className="text-zinc-500"> · </span>
                      <span>
                        {formatShortDate(preview.startedAt)} – {formatShortDate(preview.endedAt)}
                      </span>
                    </p>
                    <p className="text-xs text-zinc-500">
                      This is the planning label and window for your next cycle (not necessarily the
                      same as a calendar quarter).
                    </p>
                  </div>
                )}
              </div>
            </div>

            {createError && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                {createError}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createBusy}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="gradient"
                onClick={submitCreate}
                disabled={createBusy || !!previewError || !preview || previewLoading}
              >
                {createBusy ? "Starting…" : "Start this cycle"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Add your first rock</DialogTitle>
              <DialogDescription className="text-zinc-600">
                You&apos;re in{" "}
                <span className="font-semibold text-zinc-900">{newCycle?.quarterKey}</span>. Name
                one big outcome for this cycle — you can add more on the dashboard anytime (up to
                five).
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <label htmlFor="first-rock-title" className="text-sm font-medium text-zinc-800">
                Rock title
              </label>
              <Input
                id="first-rock-title"
                value={firstRockTitle}
                onChange={(e) => setFirstRockTitle(e.target.value)}
                placeholder="e.g. Launch the new service tier with 5 paying customers"
                disabled={rockBusy}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), saveFirstRock())}
              />
              {rockError && (
                <p className="text-sm text-red-800">{rockError}</p>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                className="sm:mr-auto"
                onClick={finishWithoutRock}
                disabled={rockBusy}
              >
                Skip for now
              </Button>
              <Button type="button" variant="gradient" onClick={saveFirstRock} disabled={rockBusy}>
                {rockBusy ? "Saving…" : "Save rock & done"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
